import gql from 'graphql-tag'
import { apolloClient } from '@/apollo'

export type CrossModelConflict =
  | { conflict: false }
  | {
      conflict: true
      currentEbomName: string
      newEbomName: string
      currentBopUploadId: string
      newBopUploadId: string
    }

type QueryResult = {
  product: {
    step: {
      id: string
      articleAllocations: Array<
        | { __typename: 'EbomPartAllocation'; ebomPart: { ebomId: string } }
        | { __typename: 'ManualArticleAllocation' }
      >
    }
  }
  article: {
    ebomsForBop: Array<{
      id: string
      name: string
      bopUpload: { id: string } | null
    }>
  }
}

const QUERY = gql`
  query Cross3dModelCheck($stepId: ID!, $bopId: ID!) {
    product {
      step(id: $stepId) {
        id
        articleAllocations {
          __typename
          ... on EbomPartAllocation {
            ebomPart {
              ebomId
            }
          }
        }
      }
    }
    article {
      ebomsForBop(bopId: $bopId) {
        id
        name
        bopUpload {
          id
        }
      }
    }
  }
`

/**
 * Determines whether allocating parts from one or more new EBOMs onto a step would
 * introduce a second 3D model (because the step already has allocations from EBOMs
 * tied to a different BOPUpload). Returns a conflict descriptor so the caller can
 * prompt the user, or `{ conflict: false }` when safe to proceed.
 */
export async function checkCross3dModelConflict(args: {
  stepId: string
  bopId: string
  newEbomIds: string[]
}): Promise<CrossModelConflict> {
  const { stepId, bopId, newEbomIds } = args
  if (newEbomIds.length === 0) return { conflict: false }

  const { data } = await apolloClient.query<QueryResult>({
    query: QUERY,
    variables: { stepId, bopId },
    fetchPolicy: 'no-cache',
  })

  const ebomById = new Map(data.article.ebomsForBop.map((e) => [e.id, e]))

  const newBopUploadIds = new Set(
    newEbomIds.map((id) => ebomById.get(id)?.bopUpload?.id).filter((v): v is string => !!v),
  )
  if (newBopUploadIds.size === 0) return { conflict: false }

  const existingEbomIds = new Set<string>(
    data.product.step.articleAllocations.flatMap((a) =>
      a.__typename === 'EbomPartAllocation' ? [a.ebomPart.ebomId] : [],
    ),
  )
  for (const id of newEbomIds) existingEbomIds.delete(id)
  if (existingEbomIds.size === 0) return { conflict: false }

  for (const existingEbomId of existingEbomIds) {
    const existing = ebomById.get(existingEbomId)
    const existingBopUploadId = existing?.bopUpload?.id
    if (!existingBopUploadId) continue
    if (!newBopUploadIds.has(existingBopUploadId)) {
      const newEbomName = newEbomIds
        .map((id) => ebomById.get(id)?.name)
        .filter((v): v is string => !!v)
        .join(', ')
      const newBopUploadId = newEbomIds
        .map((id) => ebomById.get(id)?.bopUpload?.id)
        .find((v): v is string => !!v) as string
      return {
        conflict: true,
        currentEbomName: existing?.name ?? '',
        newEbomName,
        currentBopUploadId: existingBopUploadId,
        newBopUploadId,
      }
    }
  }

  return { conflict: false }
}
